import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../components/config";
import AutoAssignmentService from "./AutoAssignmentService";

const AgentStatusToggle = ({ agentId, currentStatus, onStatusChange }) => {
  const [status, setStatus] = useState(currentStatus || 'active');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentStatus) {
      setStatus(currentStatus);
    }
  }, [currentStatus]);

  const updateStatus = async (newStatus) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const success = await AutoAssignmentService.updateAgentSupportStatus(agentId, newStatus);
      
      if (success) {
        setStatus(newStatus);
        
        // Show feedback toast
        if (newStatus === 'waiting') {
          AutoAssignmentService.showSuccessToast('Status Updated', 'You are now available for assignments');
        } else if (newStatus === 'busy') {
          AutoAssignmentService.showWarningToast('Status Updated', 'You are now busy - no new assignments');
        }
        
        // Notify parent component
        if (onStatusChange) {
          onStatusChange(newStatus);
        }
      } else {
        AutoAssignmentService.showErrorToast('Update Failed', 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      AutoAssignmentService.showErrorToast('Update Failed', 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case 'waiting':
        return 'bg-green-500 text-white hover:bg-green-600';
      case 'busy':
        return 'bg-red-500 text-white hover:bg-red-600';
      case 'active':
        return 'bg-blue-500 text-white hover:bg-blue-600';
      case 'inactive':
        return 'bg-gray-500 text-white hover:bg-gray-600';
      default:
        return 'bg-gray-300 text-gray-700 hover:bg-gray-400';
    }
  };

  const getStatusIcon = (statusValue) => {
    switch (statusValue) {
      case 'waiting':
        return '🟢';
      case 'busy':
        return '🔴';
      case 'active':
        return '🔵';
      case 'inactive':
        return '⚪';
      default:
        return '❓';
    }
  };

  const getStatusText = (statusValue) => {
    switch (statusValue) {
      case 'waiting':
        return 'Available';
      case 'busy':
        return 'Busy';
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="flex flex-col space-y-2">
      <label className="text-sm font-medium text-gray-700">Support Status</label>
      
      <div className="flex gap-2">
        <button
          onClick={() => updateStatus('waiting')}
          disabled={loading || status === 'waiting'}
          className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
            status === 'waiting'
              ? 'bg-green-500 text-white shadow-lg transform scale-105'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {getStatusIcon('waiting')} {getStatusText('waiting')}
        </button>
        
        <button
          onClick={() => updateStatus('busy')}
          disabled={loading || status === 'busy'}
          className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
            status === 'busy'
              ? 'bg-red-500 text-white shadow-lg transform scale-105'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {getStatusIcon('busy')} {getStatusText('busy')}
        </button>
      </div>
      
      {loading && (
        <div className="text-xs text-gray-500 animate-pulse">
          Updating status...
        </div>
      )}
      
      <div className="text-xs text-gray-600">
        Current: <span className="font-medium">{getStatusIcon(status)} {getStatusText(status)}</span>
      </div>
    </div>
  );
};

export default AgentStatusToggle;
